import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button, StatusPill } from '@agms/ui'
import type { StatusTone } from '@agms/ui'
import { PageHeader, SurfaceCard } from '../components/ui'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import api from '../lib/api'
import type { PaginatedResponse } from '../lib/api'
import { formatCurrencyWithCentsOrDash } from '../lib/format'
import {
  ACCOUNT_GROUP_REASONS,
  accountGroupReasonLabel,
} from '../lib/accountGroups'
import type { AccountGroupMemberRow, AccountGroupReason, AccountGroupRecord } from '../lib/accountGroups'

interface MerchantHit {
  id: string
  dba_name: string
  status: string
  fiserv_mid: string | null
}

function statusTone(status: string): StatusTone {
  if (status === 'active') return 'success'
  if (status === 'suspended') return 'warning'
  if (status === 'terminated') return 'danger'
  return 'neutral'
}

function StatusCounts({ group }: { group: AccountGroupRecord }) {
  const counts = group.status_counts
  return (
    <dl className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
      <div>
        <dt className="inline">Active </dt>
        <dd className="inline font-semibold text-slate-900">{counts.active}</dd>
      </div>
      <div>
        <dt className="inline">Suspended </dt>
        <dd className="inline font-semibold text-slate-900">{counts.suspended}</dd>
      </div>
      <div>
        <dt className="inline">Terminated </dt>
        <dd className="inline font-semibold text-slate-900">{counts.terminated}</dd>
      </div>
    </dl>
  )
}

const GROUP_TEXT_HELP = 'Do not type other accounts\' names or MIDs.'

function GroupFieldHelp({ id }: { id: string }) {
  return (
    <p id={id} className="mt-1 text-xs text-slate-500">
      {GROUP_TEXT_HELP}
    </p>
  )
}

function VolumeTotals({ group }: { group: AccountGroupRecord }) {
  if (group.monthly_volume_total === undefined && group.ytd_volume_total === undefined) {
    return null
  }

  return (
    <p className="text-sm text-slate-600">
      Monthly volume {formatCurrencyWithCentsOrDash(group.monthly_volume_total)}
      {' · '}
      YTD volume {formatCurrencyWithCentsOrDash(group.ytd_volume_total)}
    </p>
  )
}

function MemberList({
  members,
  onRemove,
  removePending = false,
}: {
  members: AccountGroupMemberRow[]
  onRemove?: (member: AccountGroupMemberRow) => void
  removePending?: boolean
}) {
  if (members.length === 0) {
    return <p className="text-sm text-slate-500">No accounts are listed.</p>
  }

  return (
    <ul className="space-y-2">
      {members.map((member) => (
        <li key={member.id} className="flex flex-wrap items-center gap-2 text-sm">
          <Link to={`/merchants/${member.id}`} className="font-semibold text-blue-700 hover:text-blue-900">
            {member.dba_name}
          </Link>
          <StatusPill tone={statusTone(member.status)} className="capitalize">{member.status}</StatusPill>
          {member.fiserv_mid ? (
            <span className="font-mono text-xs text-slate-500">MID {member.fiserv_mid}</span>
          ) : null}
          {onRemove && (
            <Button variant="secondary" onClick={() => onRemove(member)} disabled={removePending}>
              Remove {member.dba_name}
            </Button>
          )}
        </li>
      ))}
    </ul>
  )
}

export function AccountGroupsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasRole } = usePermissions()
  const canManage = hasRole(['management', 'super_admin'])
  const [page, setPage] = useState(1)
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState<AccountGroupReason>('same_legal_entity')
  const [error, setError] = useState<string | null>(null)

  const groupsQuery = useQuery({
    queryKey: ['account-groups', page],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<AccountGroupRecord>>('/account-groups', {
        params: { page, per_page: 50 },
      })
      return response.data
    },
  })

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<AccountGroupRecord>('/account-groups', {
        name: name.trim(),
        reason_type: reason,
        note: note.trim() || null,
      })
      return response.data
    },
    onSuccess: async (group) => {
      setName('')
      setNote('')
      setReason('same_legal_entity')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['account-groups'] })
      navigate(`/account-groups/${group.id}`)
    },
    onError: (caught) => {
      setError(apiErrorMessage(caught, 'Could not create the group.'))
    },
  })

  const groups = groupsQuery.data

  return (
    <div className="space-y-6">
      <PageHeader
        title="Account groups"
        subtitle="Accounts that belong together: the same legal entity, the same owner, or a chain."
      />

      {canManage && (
        <SurfaceCard padding="p-5">
          <h2 className="text-base font-semibold text-slate-950">New group</h2>
          <form
            className="mt-4 grid gap-3 md:grid-cols-2"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              createMutation.mutate()
            }}
          >
            <label className="text-sm text-slate-700">
              <span className="mb-1 block font-medium">Group name</span>
              <input
                aria-label="Group name"
                aria-describedby="new-group-name-help"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                required
                maxLength={120}
              />
              <GroupFieldHelp id="new-group-name-help" />
            </label>
            <label className="text-sm text-slate-700">
              <span className="mb-1 block font-medium">Reason</span>
              <select
                aria-label="Reason"
                value={reason}
                onChange={(event) => setReason(event.target.value as AccountGroupReason)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              >
                {ACCOUNT_GROUP_REASONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-700 md:col-span-2">
              <span className="mb-1 block font-medium">Note</span>
              <textarea
                aria-label="Note"
                aria-describedby="new-group-note-help"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                rows={2}
                maxLength={2000}
              />
              <GroupFieldHelp id="new-group-note-help" />
            </label>
            {error && <p className="text-sm text-rose-700 md:col-span-2" role="alert">{error}</p>}
            <div>
              <Button type="submit" disabled={createMutation.isPending || name.trim() === ''}>
                Create group
              </Button>
            </div>
          </form>
        </SurfaceCard>
      )}

      {groupsQuery.isLoading && <p className="text-sm text-slate-500">Loading groups...</p>}
      {groupsQuery.isError && (
        <p className="text-sm text-rose-700" role="alert">Could not load account groups.</p>
      )}

      {groups && groups.data.length === 0 && (
        <SurfaceCard>
          <p className="text-sm text-slate-500">No account groups are listed.</p>
        </SurfaceCard>
      )}

      {groups && groups.data.length > 0 && (
        <div className="space-y-3">
          {groups.data.map((group) => (
            <SurfaceCard key={group.id} padding="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-2">
                  <Link to={`/account-groups/${group.id}`} className="text-base font-semibold text-slate-950 hover:text-blue-800">
                    {group.name}
                  </Link>
                  <p className="text-sm text-slate-500">{accountGroupReasonLabel(group.reason_type)}</p>
                  <StatusCounts group={group} />
                  <VolumeTotals group={group} />
                </div>
              </div>
              <div className="mt-3">
                <MemberList members={group.members} />
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}

      {groups && groups.last_page > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>Page {groups.current_page} of {groups.last_page}</span>
          <div className="flex gap-2">
            <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
              Previous
            </Button>
            <Button variant="secondary" disabled={page >= groups.last_page} onClick={() => setPage((current) => current + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function AccountGroupDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasRole } = usePermissions()
  const canManage = hasRole(['management', 'super_admin'])
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState<AccountGroupReason>('same_legal_entity')
  const [searchInput, setSearchInput] = useState('')
  const [hits, setHits] = useState<MerchantHit[]>([])
  const [error, setError] = useState<string | null>(null)
  const [searching, setSearching] = useState(false)

  const groupQuery = useQuery({
    queryKey: ['account-groups', 'detail', id],
    queryFn: async () => {
      const response = await api.get<AccountGroupRecord>(`/account-groups/${id}`)
      return response.data
    },
    enabled: Boolean(id),
  })

  const group = groupQuery.data

  useEffect(() => {
    if (!group) return
    setName(group.name)
    setNote(group.note ?? '')
    if (ACCOUNT_GROUP_REASONS.some((item) => item.value === group.reason_type)) {
      setReason(group.reason_type as AccountGroupReason)
    }
  }, [group])

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['account-groups'] })
    await queryClient.invalidateQueries({ queryKey: ['merchants'] })
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const response = await api.patch<AccountGroupRecord>(`/account-groups/${id}`, {
        name: name.trim(),
        reason_type: reason,
        note: note.trim() || null,
      })
      return response.data
    },
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (caught) => {
      setError(apiErrorMessage(caught, 'Could not save the group.'))
    },
  })

  const addMutation = useMutation({
    mutationFn: async (merchantId: string) => {
      await api.post(`/account-groups/${id}/members`, { merchant_id: merchantId })
    },
    onSuccess: async () => {
      setError(null)
      setHits([])
      setSearchInput('')
      await invalidate()
    },
    onError: (caught) => {
      setError(apiErrorMessage(caught, 'Could not add that account.'))
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (merchantId: string) => {
      await api.delete(`/account-groups/${id}/members/${merchantId}`)
    },
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (caught) => {
      setError(apiErrorMessage(caught, 'Could not remove that account.'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/account-groups/${id}`)
    },
    onSuccess: async () => {
      await invalidate()
      navigate('/account-groups')
    },
    onError: (caught) => {
      setError(apiErrorMessage(caught, 'Could not delete the group.'))
    },
  })

  const searchAccounts = async (event: FormEvent) => {
    event.preventDefault()
    const search = searchInput.trim()
    if (!search) return
    setSearching(true)
    setError(null)
    try {
      const response = await api.get<PaginatedResponse<MerchantHit>>('/merchants', {
        params: { search, per_page: 8 },
      })
      setHits(response.data.data)
    } catch (caught) {
      setError(apiErrorMessage(caught, 'Could not search accounts.'))
    } finally {
      setSearching(false)
    }
  }

  if (groupQuery.isLoading) {
    return <p className="py-8 text-center text-sm text-slate-500">Loading group...</p>
  }

  if (groupQuery.isError || !group) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Account group"
          subtitle="This group is not available."
          action={<Link to="/account-groups" className="text-sm font-semibold text-blue-700 hover:text-blue-900">Back to Account groups</Link>}
        />
      </div>
    )
  }

  const listed = group.status_counts.active + group.status_counts.suspended + group.status_counts.terminated
  const empty = group.members.length === 0 && listed === 0
  const memberIds = new Set(group.members.map((member) => member.id))

  return (
    <div className="space-y-6">
      <PageHeader
        title={group.name}
        subtitle={accountGroupReasonLabel(group.reason_type)}
        action={<Link to="/account-groups" className="text-sm font-semibold text-blue-700 hover:text-blue-900">Back to Account groups</Link>}
      />

      <SurfaceCard padding="p-5">
        <div className="space-y-2">
          <StatusCounts group={group} />
          <VolumeTotals group={group} />
          {canManage && group.note ? <p className="text-sm text-slate-600">{group.note}</p> : null}
        </div>
        <div className="mt-4">
          <MemberList
            members={group.members}
            onRemove={canManage ? (member) => removeMutation.mutate(member.id) : undefined}
            removePending={removeMutation.isPending}
          />
        </div>
      </SurfaceCard>

      {canManage && (
        <SurfaceCard padding="p-5">
          <h2 className="text-base font-semibold text-slate-950">Edit group</h2>
          <form
            className="mt-4 grid gap-3 md:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault()
              saveMutation.mutate()
            }}
          >
            <label className="text-sm text-slate-700">
              <span className="mb-1 block font-medium">Group name</span>
              <input
                aria-label="Group name"
                aria-describedby="edit-group-name-help"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                required
                maxLength={120}
              />
              <GroupFieldHelp id="edit-group-name-help" />
            </label>
            <label className="text-sm text-slate-700">
              <span className="mb-1 block font-medium">Reason</span>
              <select
                aria-label="Reason"
                value={reason}
                onChange={(event) => setReason(event.target.value as AccountGroupReason)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              >
                {ACCOUNT_GROUP_REASONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-700 md:col-span-2">
              <span className="mb-1 block font-medium">Note</span>
              <textarea
                aria-label="Note"
                aria-describedby="edit-group-note-help"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                rows={2}
                maxLength={2000}
              />
              <GroupFieldHelp id="edit-group-note-help" />
            </label>
            <div>
              <Button type="submit" disabled={saveMutation.isPending || name.trim() === ''}>Save group</Button>
            </div>
          </form>
        </SurfaceCard>
      )}

      {canManage && (
        <SurfaceCard padding="p-5">
          <h2 className="text-base font-semibold text-slate-950">Add an account</h2>
          <form className="mt-4 flex flex-wrap gap-2" onSubmit={searchAccounts}>
            <input
              type="search"
              aria-label="Search accounts to add"
              placeholder="Search by name or MID"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className="w-72 rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
            <Button type="submit" variant="secondary" disabled={searching || searchInput.trim() === ''}>
              Search
            </Button>
          </form>
          {hits.length > 0 && (
            <ul className="mt-4 space-y-2">
              {hits.map((hit) => (
                <li key={hit.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-slate-900">{hit.dba_name}</span>
                  <StatusPill tone={statusTone(hit.status)} className="capitalize">{hit.status}</StatusPill>
                  {hit.fiserv_mid ? <span className="font-mono text-xs text-slate-500">MID {hit.fiserv_mid}</span> : null}
                  <Button
                    variant="secondary"
                    disabled={memberIds.has(hit.id) || addMutation.isPending}
                    onClick={() => addMutation.mutate(hit.id)}
                  >
                    {memberIds.has(hit.id) ? 'Already added' : 'Add'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </SurfaceCard>
      )}

      {canManage && empty && (
        <Button
          variant="secondary"
          onClick={() => {
            if (window.confirm('Delete this empty group?')) {
              deleteMutation.mutate()
            }
          }}
          disabled={deleteMutation.isPending}
        >
          Delete group
        </Button>
      )}

      {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}
    </div>
  )
}
